import type {
  AllowanceRule,
  DeductionRule,
  MinorUnits,
} from '../../data/types';
import type { DataCentreState } from '../../data/use_data_centre';

/**
 * Rules are structured data, not prose, so rendering one means choosing a
 * translation key and its interpolation values. Returning that pair (rather
 * than a string) keeps these functions pure and locale-independent.
 */
export interface RuleLabel {
  key: string;
  amount?: MinorUnits;
  rate?: number;
  multiple_bp?: number;
}

export function allowance_rule_label(rule: AllowanceRule): RuleLabel {
  switch (rule.kind) {
    case 'fixed_monthly':
      return { key: 'data_centre.rule.fixed_monthly', amount: rule.amount };
    case 'percent_of_basic':
      return { key: 'data_centre.rule.percent_of_basic', rate: rule.rate };
    case 'hourly_multiple':
      return {
        key: 'data_centre.rule.hourly_multiple',
        multiple_bp: rule.multiple_bp,
      };
    case 'hourly_rate':
      return { key: 'data_centre.rule.hourly_rate', amount: rule.amount };
    case 'manager_input':
      return { key: 'data_centre.rule.manager_input' };
  }
}

export function deduction_rule_label(rule: DeductionRule): RuleLabel {
  switch (rule.kind) {
    case 'percent_of_basic':
      return { key: 'data_centre.rule.percent_of_basic', rate: rule.rate };
    case 'progressive_bracket':
      return { key: 'data_centre.rule.progressive_bracket' };
    case 'fixed_installment':
      return { key: 'data_centre.rule.fixed_installment', amount: rule.amount };
    // Shares `data_centre.rule.hourly_rate`/`manager_input` with
    // `allowance_rule_label` (#22834) -- neither key's text is
    // allowance-specific ("Fixed · {amount} / hour", "Variable · entered by
    // the manager" read correctly for a deduction too).
    case 'hourly_rate':
      return { key: 'data_centre.rule.hourly_rate', amount: rule.amount };
    case 'manager_input':
      return { key: 'data_centre.rule.manager_input' };
  }
}

/**
 * What the Allowances/Deductions tabs should render for a given
 * `DataCentreState` — extracted as a pure, independently-tested function,
 * mirroring `resolve_employee_directory_view`
 * (`screens/employees/employee_directory_utils.ts`): a genuine fetch-in-
 * flight, a missing-`payroll`-add-on `entitlement_required`, and a real
 * `error` must each render their own copy, never collapse into the same
 * empty table an item-less-but-`ready` entity gets. The tax/bank/loans tabs
 * are unaffected by this — they stay fully mock (out of scope for #22728)
 * and always render regardless of this state.
 */
export type DataCentreView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'ready';

export function resolve_data_centre_view(
  state: DataCentreState,
): DataCentreView {
  switch (state.status) {
    case 'loading':
      return 'loading';
    case 'no_entity':
      return 'no_entity';
    case 'entitlement_required':
      return 'entitlement_required';
    case 'error':
      return 'error';
    case 'ready':
      return 'ready';
  }
}
